/* oxlint-disable @typescript-eslint/no-explicit-any */
import { ResizeObserver } from '@juggle/resize-observer';
import {
  type SpringConfig,
  type TransitionFn,
  useTransition,
} from '@react-spring/web';
import {
  type MutableRefObject,
  type Ref,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMeasure, { type RectReadOnly } from 'react-use-measure';

import {
  ANIMATION_CONFIG_ENTER,
  ANIMATION_CONFIG_LEAVE,
  mergeRefs,
} from '../useAnimateHeight';

type Transitions = TransitionFn<
  boolean,
  {
    opacity: number;
    width: number;
    overflow: string;
  }
>;

export const useWidth = (ref: MutableRefObject<any>, bounds: RectReadOnly) => {
  return useMemo(() => {
    if (!ref || !ref?.current) {
      return 0;
    }

    const { marginLeft, marginRight } = window.getComputedStyle(
      ref.current as Element,
    );

    const marginLeftConverted = parseFloat(marginLeft) || 0;
    const marginRightConverted = parseFloat(marginRight) || 0;

    return marginLeftConverted + marginRightConverted + bounds.width;
  }, [bounds.width, ref]);
};

type UseAnimateWidthOptions = {
  config?: SpringConfig;
  skipInitialAnimation?: boolean;
};

export const useAnimateWidth = (
  isVisible: boolean,
  options: UseAnimateWidthOptions = {},
): [Ref<HTMLDivElement>, Transitions, boolean] => {
  const { config, skipInitialAnimation } = options;
  const localRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [ref, bounds] = useMeasure({ polyfill: ResizeObserver });

  const width = useWidth(localRef, bounds);

  const defaultConfig = useMemo(
    () => (isVisible ? ANIMATION_CONFIG_ENTER : ANIMATION_CONFIG_LEAVE),
    [isVisible],
  );

  // True only when the element was visible on mount with skipInitialAnimation.
  // Stays true until the first ResizeObserver measurement lands (width > 0).
  // While true, callers should not apply spring styles to the DOM — this
  // prevents a one-frame flash where the spring starts at width: 0 before
  // the measurement arrives, collapsing the sidebar and shifting the layout.
  const [isSkipping, setIsSkipping] = useState(
    () => isVisible && !!skipInitialAnimation,
  );

  useLayoutEffect(() => {
    if (isSkipping && width > 0) {
      setIsSkipping(false);
    }
  }, [width, isSkipping]);

  const transitions = useTransition(isVisible, {
    immediate: isSkipping,
    update: { width },
    from: { opacity: 0, width: 0, overflow: 'hidden' },
    enter: { opacity: 1, width, overflow: 'visible' },
    leave: { opacity: 0, width: 0, overflow: 'hidden' },
    config: config || defaultConfig,
  });

  const mergedRefs = mergeRefs([localRef, ref]);

  return [mergedRefs, transitions, isSkipping];
};
